import React, {Component} from 'react';
import {connect} from 'react-redux';
import {createStructuredSelector} from 'reselect';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  artIcon,
  namesIcon,
  printMethodIcon,
  productColorIcon,
  textIcon,
  uploadIcon
} from '~/constants/InlineSvgs';
import {isEligibleForDecoFlex} from '~/utils/catalog';
// components
import MenuButton from '~/shared/components/MenuButton';
import withRouter, {routerPropTypes} from '~/router/withRouter';
// selectors
import {
  getErrorFreeSelectedProduct,
  getSelectedProducts
} from '~/selectors/ProductSelectors';
import {
  isDragNDropAnywhereEnabled as isDragNDropAnywhereEnabledSelector,
  isAddToClipartEnabled as isAddToClipartEnabledSelector
} from '~/selectors/ConfigSelectors';
// actions
import {metrics} from '~/vendor/metrics';
import {ELIGIBLE_STYLES} from '~/constants/AssistedDesignConstants';

import './index.scss';
import FullPageDropzone from '~/components/UploadDropzone/FullPageDropzone';

const propTypes = {
  allowsNamesAndNumbers: PropTypes.bool,
  clickAD: PropTypes.func,
  clickArt: PropTypes.func,
  clickHelp: PropTypes.func,
  clickNamesAndNumbers: PropTypes.func,
  clickNotes: PropTypes.func,
  clickPrintMethod: PropTypes.func,
  clickProductColor: PropTypes.func,
  clickProductDetails: PropTypes.func,
  clickSave: PropTypes.func,
  clickShare: PropTypes.func,
  clickText: PropTypes.func,
  clickUpload: PropTypes.func,
  currentProduct: PropTypes.object,
  isAddToClipartEnabled: PropTypes.bool,
  isDragNDropAnywhereEnabled: PropTypes.bool,
  isFundraisingMode: PropTypes.bool,
  router: routerPropTypes,
  selectedProducts: PropTypes.object
};

/** @extends {Component<PropTypes.InferProps<typeof propTypes>>} */
export class VerticalToolbar extends Component {
  static displayName = 'VerticalToolbar';
  static propTypes = propTypes;

  state = {
    activeSection: window.location.hash
  };

  UNSAFE_componentWillMount() {
    window.addEventListener('hashchange', this.updateSelected);
  }

  componentDidUpdate(prevProps) {
    // react-router doesn't trigger the event listener added in component did mount so adding this did update fixes updating the current selected, TODO: transform the buttons to react-router NavLink
    if (
      prevProps.router?.location.pathname !==
      this.props.router.location.pathname
    ) {
      this.updateSelected();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('hashchange', this.updateSelected);
  }

  updateSelected = () => this.setState({activeSection: window.location.hash});

  renderButtons = items => {
    return items.map((item, index) => {
      const itemActive = new RegExp(item.section).test(
        this.state.activeSection
      );
      const classes = classnames('ndx-VerticalToolbar-item', {
        'ndx-VerticalToolbar-item--active': itemActive
      });
      return <MenuButton {...item} key={`tb${index}`} className={classes} />;
    });
  };

  getDesignItems() {
    const {allowsNamesAndNumbers, isFundraisingMode, isAddToClipartEnabled} =
      this.props;
    const designItems = [
      {
        text: 'Upload',
        section: 'upload',
        icon: uploadIcon,
        onClick: this.props.clickUpload
      },
      {
        text: 'Add Text',
        section: 'addText|textTools',
        icon: textIcon,
        onClick: this.props.clickText
      },
      {
        text: isAddToClipartEnabled ? 'Add Clipart' : 'Add Art',
        section: 'addArt|artTools|searchArt',
        icon: artIcon,
        onClick: this.props.clickArt
      },
      {
        text: 'Product Colors',
        section: 'productColor',
        icon: productColorIcon,
        onClick: this.props.clickProductColor
      }
    ];

    if (isEligibleForDecoFlex(this.props.selectedProducts)) {
      designItems.push({
        text: 'Print Method',
        section: 'printMethod',
        icon: printMethodIcon,
        onClick: () => {
          metrics.fireEvent('print method', 'lab click', 'side bar');
          this.props.clickPrintMethod();
        }
      });
    }

    if (!isFundraisingMode && allowsNamesAndNumbers) {
      designItems.push({
        text: 'Add Names',
        section: 'namesAndNumbers',
        icon: namesIcon,
        onClick: this.props.clickNamesAndNumbers
      });
    }
    return designItems;
  }

  productIneligible = () =>
    this.props.currentProduct &&
    !ELIGIBLE_STYLES.includes(this.props.currentProduct.id);

  render() {
    const designItems = this.getDesignItems();
    const {isDragNDropAnywhereEnabled} = this.props;

    return (
      <>
        <div data-testid="vertical-toolbar" className="ndx-VerticalToolbar">
          <div className="ndx-VerticalToolbar-buttonGroup">
            {this.renderButtons(designItems)}
          </div>
        </div>
        {isDragNDropAnywhereEnabled && <FullPageDropzone />}
      </>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  selectedProducts: getSelectedProducts,
  currentProduct: getErrorFreeSelectedProduct,
  isAddToClipartEnabled: isAddToClipartEnabledSelector,
  isDragNDropAnywhereEnabled: isDragNDropAnywhereEnabledSelector
});

export default withRouter(connect(mapStateToProps)(VerticalToolbar));
